import React from 'react';
import { Brain, Layout, Target } from 'lucide-react';

const ApproachSection = () => {
  const methodSteps = [
    {
      number: '01',
      title: 'Insight',
      description: 'We analyse how decisions are made, where intelligence is trapped, and how systems influence behaviour.',
      icon: Brain
    },
    {
      number: '02',
      title: 'Design',
      description: 'We create adaptive governance and organizational architectures that link accountability, data, and collaboration.',
      icon: Layout
    },
    {
      number: '03',
      title: 'Impact',
      description: 'We embed decision criteria and leadership capability that keep organizations aligned and adaptable long after the work ends.',
      icon: Target
    }
  ];

  return (
    <section id="approach" className="approach-section">
      <div className="approach-container">
        <div className="section-header">
          <h2 className="section-title">How We Think and Work</h2>
        </div>

        {/* Philosophy */}
        <div className="philosophy-block">
          <h3 className="subsection-title">Our Philosophy</h3>
          <p className="philosophy-text">
            Adaptive intelligence is the structural capacity to sustain performance during constant
            change. It is the architectural discipline of integrating human judgement, governance
            design, and data insights into a single, coherent operating system.
          </p>
          <p className="philosophy-text">
            At IDO Adaptive, we view governance, culture, risk, and technology as interdependent
            variables. We apply systems thinking to synchronize decision-making, information flow,
            and human collaboration - transforming operational complexity into strategic coherence.
          </p>
          <div className="philosophy-highlight">
            <p>Goal: To engineer and build ethically for clarity and data sovereignty, evolving decision maturity into sustained organizational agility.</p>
          </div>
        </div>

        {/* Method */}
        <div className="method-block">
          <h3 className="subsection-title">Our Method</h3>
          <p className="method-intro">
            Insight. Design. Impact.
          </p>

          <div className="method-steps">
            {methodSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div key={index} className="method-step">
                  <div className="step-header">
                    <div className="step-icon">
                      <IconComponent size={32} />
                    </div>
                    <span className="step-number">{step.number}</span>
                  </div>
                  <h4 className="step-title">{step.title}</h4>
                  <p className="step-description">{step.description}</p>
                </div>
              );
            })}
          </div>

          <div className="method-footer">
            <p className="method-result">
              We meet clients where they are in their evolution, providing the intelligence and structure
              needed for meaningful, timely action.
            </p>
            <p className="method-outcome">
              <strong>Result:</strong> faster, smarter, values-aligned decisions that endure.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApproachSection;